import type { ElementContent } from "hast";
import type { Root as MdastRoot } from "mdast";
import type { Handlers, State } from "mdast-util-to-hast";
import { defaultHandlers } from "mdast-util-to-hast";
import { markOrigin } from "~/components/mdast/buildHast";
import {
  isRecord,
  isReportReference,
  referenceToAnchorProperties,
} from "./citationLink";

/**
 * Report-specific MDAST validation + `mdast-util-to-hast` handlers (§4.1/§6.2).
 *
 * - `isReportMdastRoot` / `isEmptyMdastRoot`: whole-tree runtime guards so a
 *   malformed or empty tree falls back to the v1 renderer instead of throwing.
 * - `reportToHastHandlers`: a custom `link` handler that emits v1-identical
 *   citation anchors for valid `data.reference` links (reused unchanged by all
 *   downstream hast plugins and click delegation), degrades ordinary/placeholder
 *   links to plain anchors/text, and marks MDAST-originated links so the term
 *   pass skips parsed-Markdown-link subtrees (§6.3).
 */

/**
 * Recognised MDAST node types (mdast + gfm) that carry a `children` array. Each
 * must have a valid `children` array so `mdast-util-to-hast`'s `state.all` never
 * dereferences `undefined`.
 */
const PARENT_MDAST_TYPES: ReadonlySet<string> = new Set([
  "root",
  "paragraph",
  "heading",
  "blockquote",
  "list",
  "listItem",
  "table",
  "tableRow",
  "tableCell",
  "emphasis",
  "strong",
  "delete",
  "link",
  "linkReference",
  "footnoteDefinition",
]);

/** Recognised literal node types, which must carry a string `value`. */
const LITERAL_MDAST_TYPES: ReadonlySet<string> = new Set([
  "text",
  "inlineCode",
  "code",
  "html",
  "yaml",
]);

/** Recognised void node types with no required content field. */
const VOID_MDAST_TYPES: ReadonlySet<string> = new Set([
  "thematicBreak",
  "break",
  "definition",
  "imageReference",
  "footnoteReference",
]);

/**
 * Node types resolved by `identifier`: `mdast-util-to-hast` dereferences
 * `node.identifier` (e.g. `.toUpperCase()` / definition lookups), so a missing
 * or non-string identifier must fail the guard and fall back to v1.
 */
const IDENTIFIED_MDAST_TYPES: ReadonlySet<string> = new Set([
  "definition",
  "footnoteDefinition",
  "footnoteReference",
  "imageReference",
  "linkReference",
]);

function isRecordArray(
  value: unknown
): value is Array<Record<string, unknown>> {
  return Array.isArray(value) && value.every(isRecord);
}

/**
 * Validates a single node's type *and its required fields* (§4.1) before the
 * tree is handed to `mdast-util-to-hast`. Accepting a structurally-incomplete
 * node (e.g. a `paragraph` with no `children`, a `link`/`definition` without a
 * string `url`, or a reference node without an `identifier`) would throw
 * mid-render instead of falling back to v1, so each node category is checked
 * for the fields the to-hast handlers dereference.
 */
function isRecognisedNode(node: Record<string, unknown>): boolean {
  if (typeof node.type !== "string") return false;

  // Reference/definition nodes are resolved via a string `identifier`.
  if (
    IDENTIFIED_MDAST_TYPES.has(node.type) &&
    typeof node.identifier !== "string"
  ) {
    return false;
  }

  // `image`/`link`/`definition` are dereferenced via `node.url`.
  if (
    node.type === "image" ||
    node.type === "link" ||
    node.type === "definition"
  ) {
    if (typeof node.url !== "string") return false;
  }

  if (PARENT_MDAST_TYPES.has(node.type)) {
    if (!isRecordArray(node.children)) return false;
    return node.children.every(isRecognisedNode);
  }
  if (LITERAL_MDAST_TYPES.has(node.type)) {
    return typeof node.value === "string";
  }
  // `image` is void but validated for `url` above.
  return node.type === "image" || VOID_MDAST_TYPES.has(node.type);
}

/**
 * Recursive whole-tree guard (§4.1). Verifies the value is a proper MDAST
 * `root` whose entire node set is recognised, so the render pipeline never
 * hands `mdast-util-to-hast` an unknown/malformed node.
 */
export function isReportMdastRoot(value: unknown): value is MdastRoot {
  if (!isRecord(value)) return false;
  if (value.type !== "root") return false;
  if (!isRecordArray(value.children)) return false;
  return value.children.every(isRecognisedNode);
}

/**
 * True when a validated root has no children — an "empty" tree that renders via
 * v1 (§5.1) rather than producing an empty v2 section.
 */
export function isEmptyMdastRoot(root: MdastRoot): boolean {
  return root.children.length === 0;
}

/**
 * Extracts a renderable MDAST tree from fetched section data (§5.1). Returns the
 * `contentMdast` tree only when it is present, structurally valid and non-empty;
 * otherwise `null`, signalling a per-section fallback to the v1 renderer.
 *
 * `contentMdast` is not part of the generated `ReportSection` model, so it is
 * read defensively from the section object rather than via a generated field.
 */
export function getSectionMdast(section: unknown): MdastRoot | null {
  if (!isRecord(section)) return null;
  const candidate = section.contentMdast;
  if (!isReportMdastRoot(candidate)) return null;
  if (isEmptyMdastRoot(candidate)) return null;
  return candidate;
}

/** True for `#ref-…` placeholder links that carry no valid reference data. */
function isPlaceholderRefUrl(url: string): boolean {
  return url.startsWith("#ref-");
}

/**
 * `mdast-util-to-hast` handlers for report sections. The `link` handler maps
 * citation references to v1-identical anchors; `code`/`inlineCode` provenance
 * handlers are supplied by the shared {@link mdastToHast} builder.
 */
export const reportToHastHandlers: Partial<Handlers> = {
  link(state: State, node) {
    const reference: unknown = node.data?.reference;

    if (isReportReference(reference)) {
      const properties = referenceToAnchorProperties(reference);
      if (properties) {
        const result: ElementContent = {
          type: "element",
          tagName: "a",
          properties,
          children: state.all(node),
        };
        state.patch(node, result);
        return state.applyData(node, result);
      }
      // Valid reference but not an interactive citation (e.g. bates without a
      // click target) → render its text non-interactively (§6.2).
      return state.all(node);
    }

    // No valid reference: a `#ref-…` placeholder becomes plain text; anything
    // else is an ordinary anchor. Both are MDAST-originated → mark them so the
    // term pass skips parsed-Markdown-link subtrees (§6.3).
    if (isPlaceholderRefUrl(node.url)) {
      return state.all(node);
    }
    return markOrigin(defaultHandlers.link(state, node));
  },
};
